import * as React from 'react';

export interface ListRenderItemInfo<Item> {
    /** The item to render */
    item: Item;

    /** The index of the item in the list */
    index: number;
}

export interface ListProps<Item> extends React.ComponentPropsWithoutRef<'div'> {
    /** The list of items to render */
    items: Item[];

    /** The component to render for each item */
    renderItem: (item: ListRenderItemInfo<Item>) => React.ReactElement;

    /** The component to render as a separator between items */
    separator?: React.ComponentType | React.ReactNode;

    /** The element to render as a footer */
    footer?: React.ReactNode;
}

/**
 * Flat list of items.
 */
export const List = React.forwardRef(function List<Item>(
    props: ListProps<Item>,
    ref: React.Ref<HTMLDivElement>
): React.ReactElement {
    const { items, renderItem, separator: Separator, footer, ...rest } = props;

    return (
        <div ref={ref} role="list" {...rest}>
            {items.map((item, index) => (
                <React.Fragment key={index}>
                    {renderItem({ item, index })}
                    {Separator && index < items.length - 1 ? (
                        typeof Separator === 'function' ? (
                            <Separator />
                        ) : (
                            Separator
                        )
                    ) : null}
                </React.Fragment>
            ))}
            {footer}
        </div>
    );
}) as <Item>(props: ListProps<Item> & React.RefAttributes<HTMLDivElement>) => React.ReactElement;
